import { apiRequest } from "src/services/apiClient";

/** Staff-facing view of what the desktop agent did on its own (backend/src/routes/gdtAgentSyncRoutes.ts):
 *  recent commits that can still be undone, and imports it refused because two patients matched. */
export interface PendingAgentUndo {
    id: string;
    patientId: string;
    recipientId: string | null;
    externalId: string;
    committedAt: string;
    undoWindowExpiresAt: string;
    firstName: string;
    lastName: string;
    created: boolean;
}

export interface AmbiguousCandidate {
    patientId: string;
    firstName: string;
    lastName: string;
    birthDate: string | null;
}

export interface AmbiguousMatch {
    eventId: string;
    externalId: string;
    firstName: string;
    lastName: string;
    birthDate: string | null;
    occurrences: number;
    lastSeenAt: string;
    candidates: AmbiguousCandidate[];
}

export function listPendingAgentUndos(): Promise<PendingAgentUndo[]> {
    return apiRequest<PendingAgentUndo[]>("/gdt-agent-sync/pending-undos");
}

export function undoAgentCommit(undoId: string): Promise<{ ok: boolean }> {
    return apiRequest<{ ok: boolean }>(`/gdt-agent-sync/undo/${undoId}`, { method: "POST" });
}

export function listAmbiguousMatches(): Promise<AmbiguousMatch[]> {
    return apiRequest<AmbiguousMatch[]>("/gdt-agent-sync/ambiguous");
}

export function dismissAmbiguousMatch(eventId: string): Promise<{ ok: boolean }> {
    return apiRequest<{ ok: boolean }>(`/gdt-agent-sync/ambiguous/${eventId}/dismiss`, { method: "POST" });
}

/** Public endpoint — no session needed. `version: null` means nothing has been published yet. */
export interface AgentRelease {
    version: string | null;
    downloadUrl?: string;
    publishedAt?: string;
}

export function getLatestAgentRelease(): Promise<AgentRelease> {
    return apiRequest<AgentRelease>("/app-version/agent");
}

/** Settings → Praxis Software Sync — shared policy for both the browser folder sync and the
 *  desktop agent (backend/src/models/Settings.ts's praxisSoftwareSync group). */
export interface AgentPopupSettings {
    consent: boolean;
    invoiceSent: boolean;
    paid: boolean;
}

/** Documents the desktop agent writes into the practice software's folder. */
export interface AgentDocumentSettings {
    consent: boolean;
    invoiceSent: boolean;
    paid: boolean;
}

export interface PraxisSoftwareSyncSettings {
    autoParse: boolean;
    autoSendConsent: boolean;
    syncPaused: boolean;
    popups: AgentPopupSettings;
    documents: AgentDocumentSettings;
}

/** GET /settings returns the whole settings document; this plucks the one group this feature
 *  owns, defaulting every field to false if the document (or the group) doesn't exist yet. */
export async function getPraxisSoftwareSyncSettings(): Promise<PraxisSoftwareSyncSettings> {
    const data = await apiRequest<{ praxisSoftwareSync?: Partial<Omit<PraxisSoftwareSyncSettings, "popups" | "documents">> & { popups?: Partial<AgentPopupSettings>; documents?: Partial<AgentDocumentSettings> } }>("/settings");
    const s = data.praxisSoftwareSync ?? {};
    return {
        autoParse: !!s.autoParse,
        autoSendConsent: !!s.autoSendConsent,
        syncPaused: !!s.syncPaused,
        popups: {
            consent: s.popups?.consent !== false,
            invoiceSent: s.popups?.invoiceSent !== false,
            paid: s.popups?.paid !== false,
        },
        documents: {
            consent: s.documents?.consent !== false,
            invoiceSent: s.documents?.invoiceSent === true,
            paid: s.documents?.paid === true,
        },
    };
}

export function savePraxisSoftwareSyncSettings(settings: PraxisSoftwareSyncSettings): Promise<{ praxisSoftwareSync: PraxisSoftwareSyncSettings }> {
    return apiRequest<{ praxisSoftwareSync: PraxisSoftwareSyncSettings }>(
        "/settings/praxisSoftwareSync",
        { method: "PUT", body: JSON.stringify(settings) },
        { "Content-Type": "application/json" },
    );
}
